import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { actionLabel, api, relTime, type Me } from '../lib/api'

const PAGE = 50

/**
 * The account's own audit trail.
 *
 * Deliberately customer-visible. An account owner should be able to see that a colleague
 * revoked a key or removed a suppression without opening a support ticket, and sign-in
 * history is the first thing anyone wants when they suspect a compromise — at which point
 * "email support and wait" is the wrong answer.
 */
export function Activity({ me }: { me: Me }) {
  const [page, setPage] = useState(0)

  const { data, isLoading } = useQuery({
    queryKey: ['activity', page],
    queryFn: () => api.activity.list(PAGE, page * PAGE),
  })

  const rows = data?.data ?? []
  const total = data?.total ?? 0
  const hasMore = (page + 1) * PAGE < total

  // API request activity — every call this account's API keys made, including the invalid ones.
  const [apiErrorsOnly, setApiErrorsOnly] = useState(false)
  const { data: apiData } = useQuery({
    queryKey: ['activity', 'api', apiErrorsOnly],
    queryFn: () => api.activity.apiRequests({ limit: 50, errorsOnly: apiErrorsOnly }),
  })
  const apiRows = apiData?.data ?? []
  const authFailures = apiData?.auth_failures ?? []

  return (
    <div>
      <h1>Activity</h1>
      <p className="lede">
        Everything that changed on this account, and who changed it. Append-only — these
        rows cannot be edited or deleted, including by us.
      </p>

      <h2>Recent sign-ins</h2>
      {data?.recent_sign_ins.length
        ? (
          <div className="card flush">
            <table className="data">
              <thead>
                <tr><th>When</th><th>Result</th><th>IP</th><th>Account</th></tr>
              </thead>
              <tbody>
                {data.recent_sign_ins.map((s, i) => (
                  <tr key={i}>
                    <td className="small" title={new Date(s.occurred_at).toLocaleString()}>
                      {relTime(s.occurred_at)}
                    </td>
                    <td>
                      <span className={`badge ${s.result === 'success' ? 'ok' : 'critical'}`}>
                        {s.result === 'success' ? 'Success' : 'Failed'}
                      </span>
                    </td>
                    <td className="mono small">
                      {s.source_ip}
                      {s.source_country && <span className="muted"> · {s.source_country}</span>}
                    </td>
                    <td className="small muted truncate">{s.identity ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
        : <div className="hint">No sign-in history recorded yet.</div>}

      <div className="alert info" style={{ marginTop: 14 }}>
        <div>
          <strong>Sign-in from an IP you do not recognise?</strong> Change the password
          immediately, then revoke every API key and SMTP credential — a session and a key
          are separate credentials, and changing one does not invalidate the other.
        </div>
      </div>

      <h2>Changes</h2>
      {isLoading && <div className="skeleton" style={{ height: 260 }} />}

      {!isLoading && rows.length === 0 && (
        <div className="empty">
          <div className="big">☰</div>
          <p><strong>Nothing recorded yet</strong></p>
          <p>Adding a domain or creating a key will show up here.</p>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <ol className="audit">
            {rows.map((a, i) => (
              <li key={i}>
                <div className="a-head">
                  <span className="a-action">{actionLabel(a.action)}</span>
                  <span className="small muted" title={new Date(a.occurred_at).toLocaleString()}>
                    {relTime(a.occurred_at)}
                  </span>
                </div>
                <div className="a-meta small muted">
                  <span>{a.actor_type === 'user' ? 'by you' : `by ${a.actor_type}`}</span>
                  {a.actor_ip && <span className="mono">{a.actor_ip}</span>}
                  {a.entity_type && <span>{a.entity_type}</span>}
                </div>
                {a.reason && <div className="small a-reason">{a.reason}</div>}
                {/* Before/after are stored as JSON strings. Showing them raw is honest and
                    useful; prettifying them would risk hiding a field that matters. */}
                {(a.before || a.after) && (
                  <details className="a-detail">
                    <summary className="small">Details</summary>
                    {a.before && (
                      <div>
                        <div className="k small">Before</div>
                        <pre className="code small">{a.before}</pre>
                      </div>
                    )}
                    {a.after && (
                      <div>
                        <div className="k small">After</div>
                        <pre className="code small">{a.after}</pre>
                      </div>
                    )}
                  </details>
                )}
              </li>
            ))}
          </ol>

          <div className="pager">
            <button className="secondary" disabled={page === 0}
                    onClick={() => setPage(p => Math.max(0, p - 1))}>← Newer</button>
            <span className="small muted">
              {page * PAGE + 1}–{Math.min((page + 1) * PAGE, total)} of {total.toLocaleString()}
            </span>
            <button className="secondary" disabled={!hasMore}
                    onClick={() => setPage(p => p + 1)}>Older →</button>
          </div>
        </>
      )}

      <div className="spread" style={{ alignItems: 'center', marginTop: 8 }}>
        <h2 style={{ margin: 0 }}>API activity</h2>
        <div className="segmented" role="tablist" aria-label="Filter API activity">
          <button role="tab" aria-selected={!apiErrorsOnly} className={!apiErrorsOnly ? 'active' : ''}
                  onClick={() => setApiErrorsOnly(false)}>All requests</button>
          <button role="tab" aria-selected={apiErrorsOnly} className={apiErrorsOnly ? 'active' : ''}
                  onClick={() => setApiErrorsOnly(true)}>
            Errors only{apiData?.error_count ? ` (${apiData.error_count})` : ''}
          </button>
        </div>
      </div>
      <p className="small muted" style={{ marginTop: 6 }}>
        Every call your API keys made, with the outcome. Invalid hits — a bad key (401), an
        unverified domain (422), a wrong path (404) — show here with their error code and request id.
      </p>

      {apiRows.length === 0
        ? <div className="hint">{apiErrorsOnly ? 'No failed API requests recorded.' : 'No API requests recorded yet.'}</div>
        : (
          <div className="card flush" style={{ overflowX: 'auto' }}>
            <table className="data">
              <thead>
                <tr><th>When</th><th>Request</th><th className="num">Status</th><th>Error</th><th>IP</th><th>Request ID</th></tr>
              </thead>
              <tbody>
                {apiRows.map((r, i) => (
                  <tr key={i}>
                    <td className="small" title={new Date(r.occurred_at).toLocaleString()}>{relTime(r.occurred_at)}</td>
                    <td className="mono small truncate" style={{ maxWidth: 320 }}>
                      <span className="strong">{r.method}</span> {r.path}
                    </td>
                    <td className="num">
                      <span className={`badge ${r.status_code < 400 ? 'ok' : r.status_code < 500 ? 'pending' : 'critical'}`}>{r.status_code}</span>
                      <span className="small muted"> · {r.duration_ms}ms</span>
                    </td>
                    <td className="small mono crit">{r.error_code ?? '—'}</td>
                    <td className="mono small">{r.source_ip}</td>
                    <td className="mono small muted truncate" style={{ maxWidth: 180 }}>{r.request_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {authFailures.length > 0 && (
        <>
          <h3 style={{ marginTop: 18 }}>Rejected authentications</h3>
          <p className="small muted" style={{ marginTop: -4 }}>
            API-key attempts that were refused before reaching an endpoint — a revoked/expired key,
            a suspended account, or a source IP that is not on the key's allow-list.
          </p>
          <div className="card flush">
            <table className="data">
              <thead><tr><th>When</th><th>Reason</th><th>Detail</th><th>IP</th></tr></thead>
              <tbody>
                {authFailures.map((a, i) => (
                  <tr key={i}>
                    <td className="small" title={new Date(a.occurred_at).toLocaleString()}>{relTime(a.occurred_at)}</td>
                    <td><span className="badge critical">{a.result}</span></td>
                    <td className="small mono muted">{a.detail ?? '—'}</td>
                    <td className="mono small">{a.source_ip}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <h2>Account</h2>
      <div className="card flush">
        <table className="kv">
          <tbody>
            <tr><td>Account</td><td><strong>{me.tenant.name}</strong></td></tr>
            <tr><td>Account ID</td><td className="mono small">{me.tenant.id}</td></tr>
            <tr><td>Signed in as</td><td className="mono small">{me.user.email}</td></tr>
            <tr><td>Role</td><td>{me.user.role ?? '—'}</td></tr>
            <tr><td>Plan</td><td>{me.plan?.name ?? '—'}</td></tr>
            <tr>
              <td>Status</td>
              <td>
                {me.tenant.status === 'active' ? 'Active'
                  : me.tenant.status === 'sandbox' ? 'Sandbox'
                  : me.tenant.status === 'pending_review' ? 'Awaiting review'
                  : me.tenant.status}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}
